import React from "react"
import { useReviews } from "../context/ReviewContext"
import { transformImageUrl } from "../components/SafeSiteImage"
import { Helmet } from "react-helmet"

export function breadcrumbs(page) {
  let productCategoryBreadcrumbs = []

  if (page.primaryProductCategory?.name && page.primaryProductCategory?.uri) {
    if (page.primaryProductCategory?.wpParent?.name && page.primaryProductCategory?.wpParent?.uri) {
      productCategoryBreadcrumbs.push({
        text: page.primaryProductCategory.wpParent.name,
        url: page.primaryProductCategory.wpParent.uri
      })
    }
    productCategoryBreadcrumbs.push({
      text: page.primaryProductCategory.name,
      url: page.primaryProductCategory.uri
    })
  } else if (page.productCategories?.nodes && page.productCategories.nodes.length > 0) {
    productCategoryBreadcrumbs.push({
      text: page.productCategories.nodes[0].name,
      url: page.productCategories.nodes[0].uri
    })
  }

  return [
    {
      text: "Home",
      url: "/",
    },
    ...productCategoryBreadcrumbs,
    {
      text: page.title,
      url: page.uri,
    },
  ]
}

export function schema(page, site) {
  const product = page.productData;

  if (!product) {
    return
  }

  const currentReviews = reviews(page.id)

  const reviewRatings = []
  currentReviews.forEach((review) => {
    const rating = review.node.customerReviews.reviewRating
    reviewRatings.push(parseInt(rating, 10))
  })

  const average = (reviewRatings) => {
    let sum = reviewRatings.reduce(
      (previous, current) => (current += previous)
    )
    let avg = sum / reviewRatings.length

    return avg.toFixed(2).replace(".00", "")
  }

  return (
    <Helmet>
      {product.productMeta.hirePrice?.price && (<meta name="custom-hire-price" content={product.productMeta.hirePrice.price} />)}
      {product.productMeta.buyPrice?.price && (<meta name="custom-buy-price" content={product.productMeta.buyPrice.price} />)}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org/", 
            "@type": "Product", 
            "name": "${page.title}",
            "image": [
              ${
                product.productGallery && product.productGallery.length >= 1
                  ? product.productGallery.map(
                      (image) =>
                        `"${transformImageUrl({ siteUrl: site.siteMetadata.siteUrl, imageUrl: image.mediaItemUrl })}"`
                    )
                  : `"${transformImageUrl({ siteUrl: site.siteMetadata.siteUrl, imageUrl: page.featuredImage.node.mediaItemUrl })}"`
              }
            ],
            "description": "${Array.isArray(product.summary) && product.summary.length > 0 && product.summary[0]?.content ? product.summary[0].content.replace(/(<([^>]+)>)/gi, "") : ""}",
            "brand": "${site.siteMetadata.brand ?? ""}"
            ${
              product.productMeta.hirePrice?.price || product.productMeta.buyPrice?.price
                ?
                  `
                  ,"offers": {
                    "@type": "Offer",
                    "itemCondition": "https://schema.org/NewCondition",
                    "price": "${product.productMeta.buyPrice.price ?? product.productMeta.hirePrice.price}",
                    "priceCurrency": "GBP",
                    "availability": "${product.productMeta.stackStatus ?? true ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}"
                  }
                  `
                :
                  ''
            }
            ${
              currentReviews.length >= 1
                ? `
                  ,"aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "${average(reviewRatings)}",
                    "bestRating": "5",
                    "worstRating": "0",
                    "ratingCount": "${currentReviews.length}",
                    "reviewCount": "${currentReviews.length}"
                  },
                  "review": [
                    ${currentReviews.map(
                      (reviewItem) =>
                        `{
                          "@type": "Review",
                          "name": "${reviewItem.node.title}",
                          "reviewBody": "${reviewItem.node.customerReviews.reviewText}",
                          "reviewRating": {
                            "@type": "Rating",
                            "ratingValue": "${reviewItem.node.customerReviews.reviewRating}",
                            "bestRating": "5",
                            "worstRating": "0"
                          },
                          "datePublished": "${reviewItem.node.customerReviews.reviewDate}",
                          "author": {"@type": "Person", "name": "${reviewItem.node.customerReviews.reviewSource}"}
                      }`
                    )}
                    ]}`
                :
                  `}`
          }
        `}
      </script>
    </Helmet>
  )
}

export function reviews(productId) {
  const reviewsData = useReviews();
  return reviewsData.allWpReview.edges
    .filter(function (review) {
      if (review.node.customerReviews.reviewProduct) {
        const reviewProductId = review.node.customerReviews.reviewProduct.id;
        const attachedReviews =
          review.node.customerReviews.attachToAdditionalProducts;
        if (reviewProductId || attachedReviews) {
          if (reviewProductId === productId) {
            return true;
          } else if (
            attachedReviews &&
            attachedReviews.some((e) => e.id === productId)
          ) {
            return true;
          } else {
            return false;
          }
        }
      }
    })
    .sort(function (a, b) {
      const aDateString = a.node.customerReviews.reviewDate;
      const aDateParts = aDateString.split("/");
      const aDateObject = new Date(
        +aDateParts[2],
        aDateParts[1] - 1,
        +aDateParts[0]
      );

      const bDateString = b.node.customerReviews.reviewDate;
      const bDateParts = bDateString.split("/");
      const bDateObject = new Date(
        +bDateParts[2],
        bDateParts[1] - 1,
        +bDateParts[0]
      );
      return bDateObject - aDateObject;
    });
}
